import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Injectable, OnDestroy} from '@angular/core';
import {Router} from '@angular/router';
import {NullValidationHandler, OAuthService} from 'angular-oauth2-oidc';
import {CookieService} from 'ngx-cookie-service';
import {BehaviorSubject, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {EnvService} from '../../models/env.service';
import {ConnectResponse} from '../models/login/login-response';
import {Store} from '@ngxs/store';
import { UserService } from './user.service';
import {MonitoringService} from '../../services/logging.service';

@Injectable({
  providedIn: 'root'
})
export class LoginService implements OnDestroy {
  private auth_token: string;
  private username: string;
  private password: string;

  private readonly isLoggedIn = new BehaviorSubject<boolean>(false);
  private destroy$ = new Subject<boolean>();
  public isLoggedIn$ = this.isLoggedIn.asObservable();

  public userPermissions = null;

  constructor(private http: HttpClient,
    private cookieService: CookieService,
    private oauthService: OAuthService,
    private router: Router,
    private env: EnvService,
    private readonly store: Store,
    private readonly userService: UserService,
    private monitoringService: MonitoringService,
    ) {
    this.configure();
  }

  public async logoutUser() {
    this.oauthService.logOut();
    this.oauthService.resetImplicitFlow();
    this.oauthService.restartSessionChecksIfStillLoggedIn();
    this.oauthService.revokeTokenAndLogout();
    localStorage.removeItem('operatorEmail');
  }

  public async initiateLogin() {
    try {
      await this.oauthService.loadDiscoveryDocument(this.env.discoveryDocumentConfig.url);
      await this.oauthService.tryLoginImplicitFlow();
    } catch (error) {
      this.monitoringService.logException(error);
    } finally {
      // tslint:disable-next-line:no-unsafe-finally
      if (!this.oauthService.hasValidAccessToken()) { return this.oauthService.initLoginFlow(); }

      this.router.navigate(['./doors']);
    }
    // put operator email in local storage for MonitoringService
    localStorage.setItem('operatorEmail',JSON.parse( localStorage.getItem('id_token_claims_obj')).email);
  }

  public async tryHandleLoginRedirection() {
    // See if the hash fragment contains tokens (when user got redirected back)
    await this.oauthService.loadDiscoveryDocument(this.env.discoveryDocumentConfig.url);
    await this.oauthService.tryLogin();
    await this.oauthService.tryLoginImplicitFlow();
    if (!this.oauthService.hasValidAccessToken()) {
      await this.oauthService.silentRefresh();
      return;
    }

    this.oauthService.setupAutomaticSilentRefresh();

    this.monitoringService.logTrace('user login',{appVersion: this.env.environment.appVersion});

    await this.router.navigate(['./doors']);
  }

  public login(username: string, password: string) {
    this.setUserCredentials(username, password);
    return this.connect(username, password);
  }

  public setAuthToken(token: string) {
    this.auth_token = token;
    this.cookieService.set('access_token', token);
  }

  public connect(username: string, password: string) {
    let options = {
      headers: new HttpHeaders().set(
        'Content-Type',
        'application/x-www-form-urlencoded'
      )
    };
    let requestBody = new URLSearchParams();
    requestBody.set('Email', username);
    requestBody.set('Password', password);

    return this.http.post<ConnectResponse>(
      'https://localhost:44386/account/login',
      requestBody.toString(),
      options
    );
  }

  private async configure() {
    this.oauthService.events
    .pipe(takeUntil(this.destroy$))
    .subscribe(() => {
        const loggedIn = this.oauthService.hasValidAccessToken();
        if (loggedIn !== this.isLoggedIn.value) {
          this.isLoggedIn.next(loggedIn);
        }
      }
    );

    this.oauthService.configure(this.env.authConfig);
    this.oauthService.tokenValidationHandler = new NullValidationHandler();
    await this.oauthService.loadDiscoveryDocument(this.env.discoveryDocumentConfig.url);
  }

  private setUserCredentials(username: string, password: string) {
    this.username = username;
    this.password = password;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

}
